// src/lib/lead/actions/mergeLeadIntoCurrent.ts
import type { ExtractedLeadV2, Lead, LockedFields } from "@/types/lead";
import type { TextHeuristics } from "@/lib/lead/extraction/textHeuristics";

export type MergeSuggestion = {
  sourceId: string;
  reason: string;
};

export type MergeLeadIntoCurrentDeps = {
  // data access
  getCurrentLead: () => Lead | null;
  getLeadsPool: () => Lead[];

  // extraction + merge helpers
  extract: (text: string) => Promise<ExtractedLeadV2>;
  textHeuristics: Pick<TextHeuristics, "mergePreferExistingNonBlank" | "normalizePhone">;

  // optional fallbacks from LeadWorkspace if you still use them there
  isMeaningful?: (v: any) => boolean;
  mergeExtractedPreferNonEmpty?: (
    newer?: ExtractedLeadV2 | null,
    older?: ExtractedLeadV2 | null
  ) => ExtractedLeadV2 | null;

  // state writers
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  setCurrentLead: (l: Lead | null) => void;

  // transcript writers
  setTranscript: (t: string) => void;
  transcriptRef: React.MutableRefObject<string>;

  // listening writers
  setIsListening: (b: boolean) => void;
  listeningRef: React.MutableRefObject<boolean>;

  // post-merge UI
  setMergeSuggestion: (m: MergeSuggestion | null) => void;
  findMergeSuggestionForCurrent: () => MergeSuggestion | null;

  // modal cleanup
  setPendingSendLead: (v: any) => void;
  setDuplicateMatches: (v: any) => void;
  setDuplicateReason: (v: any) => void;

  /** When set, persisted source rows are removed from Supabase after merge (UUID ids only). */
  purgeLeads?: (ids: string[]) => void | Promise<void>;

  // UI confirm
  confirm?: (msg: string) => boolean;
};

const LEAD_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const defaultConfirm = (msg: string) => window.confirm(msg);

export async function mergeLeadIntoCurrentAction(
  sourceId: string,
  opts: { skipConfirm?: boolean } | undefined,
  deps: MergeLeadIntoCurrentDeps
) {
  const target = deps.getCurrentLead();
  if (!target) return;
  if (target.isTest) return;
  if (target.id === sourceId) return;

  const pool = deps.getLeadsPool();
  const source = pool.find((l) => l.id === sourceId);
  const baseTarget = pool.find((l) => l.id === target.id) || target;

  if (!source || !baseTarget) return;

  if (!opts?.skipConfirm) {
    const sourceName =
      source.name ||
      [source.extracted?.first_name, source.extracted?.last_name].filter(Boolean).join(" ") ||
      "the selected lead";

    const ok = (deps.confirm ?? defaultConfirm)(
      `Merge ${sourceName} into the current lead?\n\nThis will combine transcripts and re-run extraction, preserving existing populated fields against blank overwrites.`
    );
    if (!ok) return;
  }

  // ---------- Determine transcript ordering (chronological) ----------
  const newer = source.lastUpdated > baseTarget.lastUpdated ? source : baseTarget;
  const older = newer === source ? baseTarget : source;

  // ---------- Merge transcripts (chronological, with headers) ----------
  const header = (label: string, lead: Lead) => {
    const name =
      lead.name ||
      [lead.extracted?.first_name, lead.extracted?.last_name].filter(Boolean).join(" ") ||
      "Unknown";
    const when = new Date(lead.lastUpdated).toLocaleString();
    return `[${label}] ${name} • Updated ${when}`;
  };

  const parts: string[] = [];
  if (older.transcript && older.transcript.trim()) {
    parts.push(`${header("Call 1 - earlier", older)}\n${older.transcript.trim()}`);
  }
  if (newer.transcript && newer.transcript.trim() && newer.transcript.trim() !== older.transcript?.trim()) {
    parts.push(`${header("Call 2 - later", newer)}\n${newer.transcript.trim()}`);
  }

  const mergedTranscript = parts.join("\n\n--- merged transcript ---\n\n").trim();

  // ---------- Re-extract from merged transcript ----------
  let compositeExtracted: ExtractedLeadV2 | null = null;
  try {
    if (mergedTranscript) compositeExtracted = await deps.extract(mergedTranscript);
  } catch (e) {
    console.warn("Composite extraction failed during merge; falling back.", e);
    compositeExtracted = null;
  }

  // ---------- Merge extracted (target wins; never overwrite with blanks) ----------
  const baseExtracted = (baseTarget.extracted || {}) as ExtractedLeadV2;
  const incomingExtracted = (source.extracted || {}) as ExtractedLeadV2;

  let mergedExtracted: ExtractedLeadV2 | null = { ...baseExtracted };

  // Fill blanks in base using source (source only backfills)
  mergedExtracted = deps.textHeuristics.mergePreferExistingNonBlank(
    mergedExtracted as any,
    incomingExtracted as any
  ) as ExtractedLeadV2;

  // Then backfill using composite extraction
  if (compositeExtracted) {
    mergedExtracted = deps.textHeuristics.mergePreferExistingNonBlank(
      mergedExtracted as any,
      compositeExtracted as any
    ) as ExtractedLeadV2;
  }

  // Final fallback if somehow empty
  if (deps.isMeaningful && deps.mergeExtractedPreferNonEmpty && !deps.isMeaningful(mergedExtracted)) {
    mergedExtracted = deps.mergeExtractedPreferNonEmpty(baseTarget.extracted, source.extracted);
  }

  // Notes: append unique, avoid wiping
  if (mergedExtracted) {
    const notesParts = [
      baseTarget.extracted?.notes || "",
      newer.extracted?.notes || "",
      older.extracted?.notes || "",
      compositeExtracted?.notes || "",
    ]
      .map((s) => (s || "").trim())
      .filter(Boolean);

    const combinedNotes = Array.from(new Set(notesParts)).join("\n\n--- merged notes ---\n\n");

    if (combinedNotes.trim()) {
      mergedExtracted = { ...mergedExtracted, notes: combinedNotes };
    }

    deps.setPendingSendLead(null);
    deps.setDuplicateMatches([]);
    deps.setDuplicateReason(null);
  }

  // ---------- Lead name: sync to extracted full name if present ----------
  const extractedFullName = mergedExtracted
    ? [mergedExtracted.first_name, mergedExtracted.last_name].filter(Boolean).join(" ").trim()
    : "";

  const finalName =
    extractedFullName || baseTarget.name || newer.name || older.name || "New Customer";

  const targetLocks = (baseTarget.lockedFields || {}) as LockedFields;

  // ---------- Build merged lead ----------
  const mergedLead: Lead = {
    ...baseTarget,
    ...newer,
    id: baseTarget.id,
    name: finalName,
    transcript: mergedTranscript,
    extracted: mergedExtracted,
    lastUpdated: Date.now(),

    sent: baseTarget.sent,
    queued: baseTarget.queued,
    sendError: baseTarget.sendError ?? null,

    isTest: baseTarget.isTest || false,
    lockedFields: targetLocks,
  };

  deps.setLeads((prev) =>
    prev.filter((l) => l.id !== source.id && l.id !== baseTarget.id).concat(mergedLead)
  );

  deps.setCurrentLead(mergedLead);
  deps.setTranscript(mergedLead.transcript);
  deps.transcriptRef.current = mergedLead.transcript;

  deps.setIsListening(false);
  deps.listeningRef.current = false;

  deps.setMergeSuggestion(deps.findMergeSuggestionForCurrent());

  if (deps.purgeLeads && LEAD_ID_UUID_RE.test(source.id)) {
    await Promise.resolve(deps.purgeLeads([source.id]));
  }
}